// La fiche livreur (design 2), opened from a row of « Livreurs », a line of
// « Journée » or a point of « À faire »: the list on the left, the full file
// on the right, with MyGaz's papers check.
import { useState } from 'react';
import { S } from '../../lib/strings';
import { dayLabel, fmtN, minutesBetween, rel, xaf } from '../../lib/format';
import { blocked, negativeTag, rating, signedN, tagWord } from '../../lib/agences';
import { prettyPhone, telHref, waHref } from '../../lib/phone';
import type { Source } from '../../lib/api';
import type { Agences, AgRider, RiderFile as File } from '../../lib/types';
import { Ic } from '../Icon';
import { SkDetail } from '../Skeleton';
import { ErrorState } from '../Busy';
import { useResource } from '../../lib/useResource';
import { AccountPill, Av, RiderPill, Star, WeekBars, last7 } from './Bits';

function ListRow({ r, on, onClick }: { r: AgRider; on: boolean; onClick(): void }) {
  const warn = blocked(r) || r.state === 'invited';
  return (
    <button className={'ag-li' + (on ? ' on' : '')} onClick={onClick} aria-current={on || undefined}>
      <Av name={r.name} size={34} />
      <span className="ag-lt"><b>{r.name}</b><span className="muted">{r.rating != null ? <><Star s={11} /> {rating(r.rating)} · {S.ag.file.nTrips(r.trips)}</> : S.ag.file.noTrip}</span></span>
      {warn ? <span className="ag-warn"><Ic n="alert" s="xs" />{S.ag.tbl.todo}</span> : <RiderPill r={r} />}
    </button>
  );
}

function StarBars({ dist }: { dist: number[] }) {
  const tot = Math.max(1, dist.reduce((a, b) => a + b, 0));
  return <div className="ag-sbars">{dist.map((c, i) => (
    <div className="ag-sb" key={i}><span>{5 - i} ★</span><span className="tr"><i style={{ width: (c / tot) * 100 + '%' }} /></span><span className="c num">{c}</span></div>
  ))}</div>;
}

export function RiderFile({ data, riderId, src, onBack, onRider, openOrder, onChanged }: {
  data: Agences; riderId: string | null; src: Source; onBack(): void; onRider(id: string): void; openOrder(id: string): void; onChanged(): void;
}) {
  // Another rider drops the file on screen at once: the outline shows, never the previous rider's file.
  const R = useResource<File>(riderId ? () => src.rider(riderId) : null, [riderId, src]);
  const file = R.data;
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<boolean | null>(null);
  const [actErr, setActErr] = useState<string | null>(null);

  const check = async (ok: boolean) => {
    if (!file) return;
    setBusy(ok); setActErr(null);
    try { await src.checkPapers(file.id, ok); const next = await src.rider(file.id); R.set(() => next); onChanged(); }
    catch (e) { setActErr(String((e as Error)?.message ?? e)); }
    finally { setBusy(null); }
  };

  const match = (r: AgRider) => !q || (r.name + ' ' + (r.plate ?? '') + ' ' + (r.phone ?? '')).toLowerCase().includes(q.toLowerCase());
  const lowest = (() => {
    const xs = data.agencies.flatMap((a) => a.riders).filter((r) => r.rating != null && r.reviews >= 3 && r.state === 'active');
    return xs.length > 1 ? xs.reduce((m, x) => ((x.rating ?? 9) < (m.rating ?? 9) ? x : m)).id : null;
  })();

  let det: React.ReactNode;
  if (!riderId) det = <div className="empty"><b>{S.ag.file.choose}</b></div>;
  else if (R.status === 'error') det = <ErrorState inline error={R.error!} onRetry={R.reload} retrying={R.retrying} />;
  else if (!file) det = <SkDetail />;
  else {
    const f = file;
    const watch = f.state === 'active' && (f.cancelled_30d >= 3 || (lowest === f.id && (f.rating ?? 5) < 4.5));
    const why = [lowest === f.id ? S.ag.file.lowest : null, f.cancelled_30d ? S.ag.file.cancels(f.cancelled_30d) : null].filter(Boolean).join(S.ag.and);
    const docAt = (k: string) => f.documents.find((d) => d.kind === k)?.at;
    const tags = f.tags.slice(0, 6);
    det = (
      <>
        <div className="ag-dh">
          <Av name={f.name} size={72} />
          <div className="ag-dt"><h2>{f.name}</h2><p className="muted">{[f.agency.name, S.ag.file.since(dayLabel(f.joined_at, { day: 'numeric', month: 'long', year: 'numeric' })), f.phone ? prettyPhone('+' + f.phone) : null].filter(Boolean).join(' · ')}</p></div>
          <div className="ag-dacts">
            {f.phone && <a className="btn sm" href={telHref(f.phone)}><Ic n="phone" s="xs" />{S.ag.file.call}</a>}
            {f.phone && <a className="ibtn" href={waHref(f.phone)} target="_blank" rel="noreferrer" aria-label={S.wa} title={S.waHint}><Ic n="msg" s="s" /></a>}
          </div>
        </div>
        {f.busy && (
          <div className="ag-now"><RiderPill r={f} /><div className="t"><b>{f.busy.ref}</b> · {f.busy.kind === 'first_install' ? S.agency.ord.install : S.agency.ord.refill}{f.busy.quartier ? ` · ${f.busy.quartier}` : ''}</div>
            <button className="btn sm pri" onClick={() => openOrder(f.busy!.order_id)}>{S.ag.file.openOrder}</button></div>
        )}
        {watch && <div className="ag-now warn"><Ic n="alert" s="s" /><div className="t"><b>{S.ag.file.watch}{S.ag.file.watchWhy(why)}</b>{S.ag.file.watchTail}</div></div>}
        {blocked(f) && !watch && <div className="ag-now warn"><Ic n="alert" s="s" /><div className="t"><b>{S.ag.t.offline(f.name)}</b> {f.missing.length ? S.ag.t.missing(f.missing.map((k) => S.ag.docs[k]).join(', ')) : ''} {f.consent !== 'given' ? S.ag.t.consent : ''}</div></div>}
        <div className="ag-s4">
          <div><span className="cap">{S.ag.file.rating}</span><b className="big">{f.rating != null ? <><Star s={20} />{rating(f.rating)}</> : '—'}</b><p className="muted">{S.ag.file.reviewsOf(f.reviews, f.trips)}</p></div>
          <div><span className="cap">{S.ag.file.trips}</span><b className="big num">{fmtN(f.trips)}</b><p className="muted">{S.ag.file.since(dayLabel(f.joined_at, { day: 'numeric', month: 'long' }))}</p></div>
          <div><span className="cap">{S.ag.file.today}</span><b className="big num">{f.today}<small>{S.ag.file.delivered(f.today)}</small></b><p className="muted">{f.cash_today ? xaf(f.cash_today) : f.seen_at ? S.team.seen(rel(minutesBetween(f.seen_at))) : S.ag.rs.never}</p></div>
          <div><span className="cap">{S.ag.file.avg}</span><b className="big num">{f.avg_min != null ? <>{f.avg_min}<small>min</small></> : '—'}</b><p className="muted">{f.others_avg_min != null && f.avg_min != null ? S.ag.file.vsOthers(f.others_avg_min) : S.ag.file.avgSub}</p></div>
        </div>
        <div className="ag-two">
          <div className="ag-blk"><h3>{S.ag.file.say}<small className="muted">{S.ag.bul.reviews(f.reviews)}</small></h3>
            <StarBars dist={f.stars} />
            {tags.length > 0 && <div className="ag-tags">{tags.map((t) => <span key={t.tag} className={'ag-tag' + (negativeTag(t.tag) ? ' neg' : '')}>{tagWord(t.tag)} <b>{t.n}</b></span>)}</div>}
            {f.comments.length ? f.comments.slice(0, 3).map((c, i) => (
              <div className="ag-cm" key={i}><div className="h"><span>{'★'.repeat(c.stars)} · {S.ag.file.household(c.quartier)}</span><span>{rel(minutesBetween(c.at))}</span></div>{c.comment ? `« ${c.comment} »` : c.tags.map(tagWord).join(', ')}</div>
            )) : <p className="muted ag-small">{S.ag.file.noWords}</p>}
          </div>
          <div className="ag-blk"><h3>{S.ag.file.week}<small className="muted">{S.ag.file.deliveries(f.week.reduce((a, b) => a + b, 0))}</small></h3>
            <WeekBars vals={f.week} labels={last7()} />
            <div className="ag-kvl">
              <div><span>{S.ag.file.cancel30}</span><b className={f.cancelled_30d >= 3 ? 'ag-bad' : ''}>{f.cancelled_30d}</b></div>
              {f.cancel_reasons.map((c) => <div key={c.reason} className="sub"><span>{S.ag.file.reasons[c.reason] ?? c.reason}</span><b>{c.n}</b></div>)}
              <div><span>{S.ag.file.declined30}</span><b>{f.declined_30d}</b></div>
              <div><span>{S.ag.file.cash}</span><b className="num">{xaf(f.cash_today)}</b></div>
            </div>
          </div>
        </div>
        <div className="ag-two">
          <div className="ag-blk"><h3>{S.ag.file.papers}<small className="muted">{S.ag.file.bySource}</small></h3>
            {(['headshot', 'id_front', 'id_back', 'licence', 'bike'] as const).map((k) => {
              const at = docAt(k);
              return <div className="ag-ck" key={k}><span className={'ag-chk ' + (at ? 'ok' : k === 'bike' ? 'na' : 'no')}><Ic n={at ? 'check' : k === 'bike' ? 'minus' : 'x'} s="xs" /></span>{S.ag.docNames[k]}
                <span className={'r' + (!at && k !== 'bike' ? ' warn' : '')}>{at ? dayLabel(at) : k === 'bike' ? S.ag.file.optional : S.ag.tbl.toAdd}</span></div>;
            })}
            <div className="ag-ck"><span className={'ag-chk ' + (f.consent === 'given' ? 'ok' : 'no')}><Ic n={f.consent === 'given' ? 'check' : 'x'} s="xs" /></span>{S.ag.file.consent}
              <span className={'r' + (f.consent !== 'given' ? ' warn' : '')}>{f.consent_at ? S.ag.file.consentGiven(dayLabel(f.consent_at)) : f.consent === 'refused' ? S.ag.tbl.refused : S.ag.tbl.pending}</span></div>
            <div className="ag-mg"><svg className="i xs" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" /></svg>
              <span>{f.papers_checked_at ? S.ag.file.checked(dayLabel(f.papers_checked_at), f.papers_checked_by) : f.papers_complete ? S.ag.file.check : S.ag.file.cantCheck}</span>
              {f.papers_checked_at ? <button className="btn xs" disabled={busy !== null} aria-busy={busy === false} onClick={() => check(false)}>{S.ag.file.undo}</button>
                : f.papers_complete ? <button className="btn xs pri" disabled={busy !== null} aria-busy={busy === true} onClick={() => check(true)}>{S.ag.file.markChecked}</button> : null}
            </div>
            {actErr && <p className="ag-msg bad" role="status">{actErr}</p>}
          </div>
          <div className="ag-blk"><h3>{S.ag.file.moto}</h3>
            <div className="ag-kvl">
              <div><span>{S.ag.file.vehicle}</span><b>{[f.vehicle === 'car' ? S.agency.edit.car : S.agency.edit.moto, f.plate].filter(Boolean).join(' · ')}</b></div>
              <div><span>{S.ag.file.installer}</span><b>{f.can_install ? S.ag.file.yes : S.ag.file.no}</b></div>
              <div><span>{S.ag.file.zones}</span><b>{f.agency.zones.join(', ') || '—'}</b></div>
              <div><span>{S.ag.file.pos}</span><b>{f.position ? rel(minutesBetween(f.position.at)) : S.ag.file.none}</b></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="ag-split">
      <div className="card ag-list">
        <button className="link ag-back" onClick={onBack}><svg className="i xs" viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>{S.ag.file.back}</button>
        <label className="ag-srch"><Ic n="search" s="xs" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={S.ag.file.search} aria-label={S.ag.file.search} /></label>
        {data.agencies.map((a) => {
          const rs = a.riders.filter(match);
          if (!rs.length) return null;
          return (
            <div key={a.agency_id}>
              <div className="ag-grp"><b>{a.name}</b><span className="num">{signedN(a.balance)} XAF <AccountPill a={a} /></span></div>
              {rs.map((r) => <ListRow key={r.id} r={r} on={r.id === riderId} onClick={() => onRider(r.id)} />)}
            </div>
          );
        })}
      </div>
      <div className="card ag-det" aria-live="polite">{det}</div>
    </div>
  );
}
